import { Injectable, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BillingService } from '../Services/billing.service';
import { DEFAULT_PAGE_SIZE } from '../Consts/list-invoices.const';
import {
    BillingCommitment,
    BillingEstimate,
    BillingInvoice,
    BillingInvoicePayment,
    BillingOverview,
    BillingPaymentMethod,
    BillingPlan,
    BillingPlanOption,
    BillingSubscription,
    ContactResponse,
    EstimateResponse,
    InvoicesResponse,
    OverviewResponse,
    PlansResponse,
    PreviewResponse,
    RetryPaymentResponse,
    SubscriptionMutationResponse,
} from '../Interfaces/api/billing.interface';
import { ResourceStateService } from '../Modules/Shared/Services/resource-state.service';

@Injectable()
export class BillingFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _billingService = inject(BillingService);

    private readonly _invoicePageIndex = signal(0);

    private readonly _overviewResource = httpResource<OverviewResponse>(() =>
        this._billingService.overview(),
    );

    private readonly _estimateResource = httpResource<EstimateResponse>(() =>
        this._billingService.estimate(),
    );

    private readonly _plansResource = httpResource<PlansResponse>(() =>
        this._billingService.plans(),
    );

    private readonly _invoicesResource = httpResource<InvoicesResponse>(() =>
        this._billingService.invoices({ page: this._invoicePageIndex(), pageSize: DEFAULT_PAGE_SIZE }),
    );

    // ACTIONS
    public setInvoicePage(pageIndex: number) {
        this._invoicePageIndex.set(Math.max(0, pageIndex));
    }

    public reloadOverview() {
        this._overviewResource.reload();
    }

    public reloadEstimate() {
        this._estimateResource.reload();
    }

    public reloadInvoices() {
        this._invoicesResource.reload();
    }

    public previewPlan(planId: string): Observable<PreviewResponse> {
        return this._billingService.previewPlan(planId);
    }

    public updatePlan(planId: string): Observable<SubscriptionMutationResponse> {
        return this._billingService.updatePlan(planId);
    }

    public cancel(): Observable<SubscriptionMutationResponse> {
        return this._billingService.cancel();
    }

    public resume(): Observable<SubscriptionMutationResponse> {
        return this._billingService.resume();
    }

    public createPaymentMethodSession(): Observable<any> {
        return this._billingService.createPaymentMethodSession();
    }

    public updateContact(email: string): Observable<ContactResponse> {
        return this._billingService.updateContact(email);
    }

    public retryPayment(): Observable<RetryPaymentResponse> {
        return this._billingService.retryPayment();
    }

    // PUBLIC READ MODELS
    readonly overview = computed<BillingOverview | null>(() => {
        if (!this._overviewResource.hasValue()) {
            return null;
        }
        return this._resourceState.valueOf(this._overviewResource)?.data ?? null;
    });

    readonly subscription = computed<BillingSubscription | null>(() => this.overview()?.subscription ?? null);
    readonly plan = computed<BillingPlan | null>(() => this.overview()?.plan ?? null);
    readonly commitment = computed<BillingCommitment | null>(() => this.overview()?.commitment ?? null);
    readonly paymentMethod = computed<BillingPaymentMethod | null>(() => this.overview()?.payment_method ?? null);
    readonly billingContact = computed<string | null>(() => this.overview()?.billing_contact ?? null);
    readonly hasAgentComponent = computed<boolean>(() => this.overview()?.has_agent_component ?? false);
    readonly isSuspended = computed<boolean>(() => this.overview()?.is_suspended ?? false);

    readonly isCanceled = computed<boolean>(() => this.subscription()?.status === 'canceled');
    readonly isPastDue = computed<boolean>(() => this.subscription()?.status === 'past_due');
    readonly isReadOnly = computed<boolean>(() => this.isSuspended() || this.isCanceled());
    readonly isPendingCancellation = computed<boolean>(() => this.subscription()?.cancel_at_period_end === true);

    readonly estimate = computed<BillingEstimate | null>(() => {
        if (!this._estimateResource.hasValue()) {
            return null;
        }
        return this._resourceState.valueOf(this._estimateResource)?.data ?? null;
    });

    readonly plans = computed<BillingPlanOption[]>(() => {
        if (!this._plansResource.hasValue()) {
            return [];
        }
        return this._resourceState.valueOf(this._plansResource)?.data?.plans ?? [];
    });

    readonly invoices = computed<BillingInvoice[]>(() => {
        if (!this._invoicesResource.hasValue()) {
            return [];
        }
        return this._resourceState.valueOf(this._invoicesResource)?.data?.invoices ?? [];
    });

    readonly invoiceCount = computed<number>(() => {
        if (!this._invoicesResource.hasValue()) {
            return 0;
        }
        return this._resourceState.valueOf(this._invoicesResource)?.data?.count ?? 0;
    });

    readonly lastFailedPayment = computed<BillingInvoicePayment | null>(
        () => this.invoices().find((invoice) => invoice.display_status === 'failed')?.latest_payment ?? null,
    );

    readonly invoicePageIndex = computed<number>(() => this._invoicePageIndex());
    readonly invoicePageSize = computed<number>(() => DEFAULT_PAGE_SIZE);

    readonly isLoading = computed(() => this._overviewResource.isLoading());
    readonly error = computed(() => this._overviewResource.error());
    readonly estimateError = computed(() => this._estimateResource.error());
    readonly isEstimateLoading = computed(() => this._estimateResource.isLoading());
    readonly isInvoicesLoading = computed(() => this._invoicesResource.isLoading());
}
